import React, { useState, useCallback } from "react";
import {
  Cancel,
  ChevronLeft,
  ChevronRight,
  Photo,
  Play,
  Video,
  Volume,
  VolumeMute,
} from "@/components/bnp-icons";
import { IconPhoto, IconVideo } from "@tabler/icons-react";

interface PhotoGridProps {
  allImages: string[];
}

const isVideoFile = (url: string): boolean => {
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
  if (!url) return false;
  return videoExtensions.some((ext) => url?.toLowerCase()?.endsWith(ext));
};

const MediaContent: React.FC<{ src?: string; className?: string }> = ({
  src,
  className,
}) => {
  if (isVideoFile(src ?? "")) {
    return <VideoContent src={src ?? ""} className={className} />;
  }
  return <img src={src} alt="Property view" className={className} />;
};

const VideoContent: React.FC<{ src: string; className?: string }> = ({
  src,
  className,
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <video
        ref={videoRef}
        src={src}
        className={`block ${className}`}
        muted={true}
        autoPlay
        loop
        playsInline
      />
      <button
        onClick={toggleMute}
        aria-label={muted ? "Unmute video" : "Mute video"}
        className="absolute top-2 right-2 z-10 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80"
      >
        {muted ? <VolumeMute /> : <Volume />}
      </button>
    </div>
  );
};

const ModalVideo: React.FC<{ src: string }> = ({ src }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);

  React.useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMuted((prev) => !prev);
  };

  return (
    <div className="relative flex max-h-full max-w-full items-center justify-center">
      <video
        ref={videoRef}
        src={src}
        className="max-h-full max-w-full object-contain"
        muted
        autoPlay
        loop
        playsInline
      />
      <button
        onClick={toggleMute}
        aria-label={muted ? "Unmute video" : "Mute video"}
        className="absolute bottom-3 right-3 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
      >
        {muted ? <VolumeMute /> : <Volume />}
      </button>
    </div>
  );
};

const ImageModal: React.FC<{
  images: string[];
  initialIndex: number;
  onClose: () => void;
}> = ({ images, initialIndex, onClose }) => {
  const count = images.length;
  const clampIndex = (index: number) =>
    count === 0 ? 0 : ((index % count) + count) % count;

  const [currentIndex, setCurrentIndex] = useState(() =>
    clampIndex(initialIndex),
  );
  const stripRef = React.useRef<HTMLDivElement>(null);
  const touchStartX = React.useRef<number | null>(null);

  // Re-sync if the opener passes a new index while mounted, and clamp if
  // the list itself changes (e.g. refetch) so we never point past the end.
  React.useEffect(() => {
    setCurrentIndex(clampIndex(initialIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIndex, count]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (count === 0 ? 0 : (prev + 1) % count));
  }, [count]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (count === 0 ? 0 : (prev - 1 + count) % count));
  }, [count]);

  const goTo = useCallback(
    (index: number) => {
      setCurrentIndex(clampIndex(index));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count],
  );

  // Keyboard navigation + body scroll lock while the preview is open.
  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, goNext, goPrev]);

  // Keep the active thumbnail in view.
  React.useEffect(() => {
    stripRef.current?.children[currentIndex]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [currentIndex]);

  const currentSrc = count === 0 ? undefined : images[currentIndex];

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/90 p-4 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Property media preview"
    >
      {/* Top bar: counter + close */}
      <div
        className="mx-auto flex w-full max-w-5xl items-center justify-between text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-sm font-medium tabular-nums">
          {count > 0 ? currentIndex + 1 : 0} / {count}
        </span>
        <button
          onClick={onClose}
          aria-label="Close preview"
          className="rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
        >
          <Cancel />
        </button>
      </div>

      {/* Stage */}
      <div
        className="relative mx-auto flex min-h-0 w-full max-w-5xl flex-1 items-center justify-center py-4"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchStartX.current;
          touchStartX.current = null;
          if (Math.abs(dx) < 40) return;
          if (dx < 0) goNext();
          else goPrev();
        }}
      >
        {count > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous media"
            className="absolute left-0 z-10 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70 sm:left-2"
          >
            <ChevronLeft />
          </button>
        )}

        {currentSrc !== undefined &&
          (isVideoFile(currentSrc) ? (
            <ModalVideo key={currentSrc} src={currentSrc} />
          ) : (
            <img
              key={currentSrc}
              src={currentSrc}
              alt={`Property preview ${currentIndex + 1} of ${count}`}
              className="max-h-full max-w-full select-none object-contain"
              draggable={false}
            />
          ))}

        {count > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next media"
            className="absolute right-0 z-10 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70 sm:right-2"
          >
            <ChevronRight />
          </button>
        )}
      </div>

      {/* Thumbnail strip */}
      {count > 1 && (
        <div
          ref={stripRef}
          className="mx-auto flex w-full max-w-5xl gap-2 overflow-x-auto pb-1"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((thumb, index) => {
            const isActive = index === currentIndex;
            return (
              <button
                key={`${thumb}-${index}`}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goTo(index);
                }}
                aria-label={`View media ${index + 1} of ${count}`}
                aria-current={isActive}
                className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-lg transition-all ${
                  isActive
                    ? "ring-2 ring-white ring-offset-2 ring-offset-black"
                    : "opacity-60 hover:opacity-100"
                }`}
              >
                {isVideoFile(thumb) ? (
                  <>
                    <video
                      src={thumb}
                      muted
                      playsInline
                      preload="metadata"
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white">
                      <Play />
                    </span>
                  </>
                ) : (
                  <img
                    src={thumb}
                    alt=""
                    loading="lazy"
                    draggable={false}
                    className="h-full w-full object-cover"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PropertyPhotoGrid: React.FC<PhotoGridProps> = ({ allImages }) => {
  const images = allImages ?? [];
  const calculatedTotalVideos = images.filter(isVideoFile).length;
  const calculatedTotalPhotos = images.length - calculatedTotalVideos;
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null,
  );

  const openImageModal = (index: number) => {
    if (!images[index]) {
      return;
    }
    setSelectedImageIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImageIndex(null);
  };

  const displayImages =
    images.length > 0 ? images : ["/assets/images/image-placeholder.jpg"];
  const hasMultipleMedia = displayImages.length > 1;

  return (
    <div className="relative lg:w-[700px] font-sans">
      {selectedImageIndex !== null && (
        <ImageModal
          images={displayImages}
          initialIndex={selectedImageIndex}
          onClose={closeImageModal}
        />
      )}

      {hasMultipleMedia && (
        <button
          type="button"
          onClick={() => openImageModal(0)}
          className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 rounded-full bg-black/60 px-3.5 py-2 text-xs font-semibold text-white backdrop-blur-sm transition-colors hover:bg-black/80"
        >
          <IconPhoto size={16} />
          Click to View All Media
        </button>
      )}

      {hasMultipleMedia ? (
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-2 h-auto lg:h-[350px] w-full">
          {/* Main Featured Media (Left Side) */}
          {displayImages[0] && (
            <div
              className="lg:col-span-7 relative w-full h-[250px] sm:h-[300px] lg:h-full cursor-pointer overflow-hidden rounded"
              onClick={() => openImageModal(0)}
            >
              <MediaContent
                src={displayImages[0]}
                className="w-full h-full object-cover rounded"
              />
            </div>
          )}

          {/* Secondary Media Grid + Counter (Right Side) */}
          <div className="lg:col-span-3 flex flex-col gap-2 justify-between h-full overflow-hidden">
            {/* Upper Thumbnail (Image #2) */}
            {displayImages[1] && (
              <div
                className="hidden lg:block relative w-full h-full cursor-pointer overflow-hidden rounded"
                onClick={() => openImageModal(1)}
              >
                <MediaContent
                  src={displayImages[1]}
                  className="w-full h-full object-cover rounded"
                />
              </div>
            )}

            {/* Bottom Dual Thumbnails (Image #3 & #4) */}
            {(displayImages[2] || displayImages[3]) && (
              <div className="hidden lg:grid grid-cols-2 gap-2 h-[100px]">
                {displayImages[2] && (
                  <div
                    className="relative w-full h-full cursor-pointer overflow-hidden rounded"
                    onClick={() => openImageModal(2)}
                  >
                    <MediaContent
                      src={displayImages[2]}
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                )}
                {displayImages[3] && (
                  <div
                    className="relative w-full h-full cursor-pointer overflow-hidden rounded"
                    onClick={() => openImageModal(3)}
                  >
                    <MediaContent
                      src={displayImages[3]}
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                )}
              </div>
            )}
            {/* Media Badges Footer */}
            <div className="flex items-center gap-4 shrink-0">
              <div className="flex items-center gap-1.5 text-gray-700">
                <IconPhoto size={18} />
                <span className="text-xs font-medium whitespace-nowrap">
                  {calculatedTotalPhotos} Photo(s)
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-700">
                <IconVideo size={18} />
                <span className="text-xs font-medium whitespace-nowrap">
                  {calculatedTotalVideos} Video(s)
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="relative w-full h-[550px] cursor-pointer"
          onClick={() => openImageModal(0)}
        >
          <MediaContent
            src={displayImages[0]}
            className="w-full h-full object-cover rounded"
          />
        </div>
      )}
    </div>
  );
};

export default PropertyPhotoGrid;
